import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Gem, BookOpen, Search, MessageCircle, User, ClipboardList } from 'lucide-react';
import { motion } from 'framer-motion';

export const BottomNav = () => {
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { unreadMessages, unreadComments } = useNotifications();
  const location = useLocation();

  // Don't show on landing page
  if (location.pathname === '/') return null;

  const tabs = [
    { path: '/home', icon: Gem, labelKey: 'app_name', testId: 'bottom-tab-home', badge: isAuthenticated ? unreadComments : 0 },
    { path: '/preaching', icon: ClipboardList, labelKey: 'preaching', testId: 'bottom-tab-preaching', badge: 0 },
    { path: '/search', icon: Search, labelKey: 'search', testId: 'bottom-tab-search', badge: 0 },
    { path: '/messages', icon: MessageCircle, labelKey: 'messages', testId: 'bottom-tab-messages', badge: unreadMessages, requiresAuth: true },
    { path: '/profile', icon: User, labelKey: 'profile', testId: 'bottom-tab-profile', badge: 0 },
  ];

  // Filter tabs based on auth state
  const visibleTabs = tabs.filter(tab => !tab.requiresAuth || isAuthenticated);
  const activeIndex = visibleTabs.findIndex(tab => location.pathname.startsWith(tab.path));

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-t border-border"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="max-w-lg mx-auto flex relative">
        {/* Active indicator */}
        {activeIndex >= 0 && (
          <motion.div
            className="absolute top-0 h-0.5 bg-primary rounded-full"
            style={{ width: `${100 / visibleTabs.length}%` }}
            animate={{ left: `${(activeIndex / visibleTabs.length) * 100}%` }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          />
        )}

        {visibleTabs.map((tab) => {
          const isActive = location.pathname.startsWith(tab.path);
          return (
            <NavLink
              key={tab.path}
              to={tab.path}
              className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 relative overflow-hidden px-1"
              data-testid={tab.testId}
            >
              <div className="relative">
                <tab.icon
                  className={`h-5 w-5 transition-colors duration-200 ${
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  strokeWidth={isActive ? 2.2 : 1.8}
                />
                {/* Badge */}
                {tab.badge > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1"
                    data-testid={`badge-${tab.testId}`}
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </motion.span>
                )}
              </div>
              <span
                className={`text-[10px] leading-tight font-medium text-center max-w-full transition-colors duration-200 ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {t(tab.labelKey)}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
